import { EventEmitter, NgZone } from '@angular/core';
import { LoadCasesFlowStep, LoadType, PartSubType, PartType, deriveMassType, effectiveReferencePoint } from '../../models/enums';
import { BoundaryConditionItem, LoadItem } from '../../models/loadcase.model';
import { SelectorObject } from '../../models/interfaces';
import { PART_BOUNDARY_CONDITION_COLOR, PART_LOAD_COLOR } from '../engine.constants';
import {
  Box3,
  BufferAttribute,
  Color,
  DoubleSide,
  Mesh,
  Object3D,
  Raycaster,
  Vector2,
  Vector3,
  BufferGeometry,
  Group,
  Sprite,
  SpriteMaterial,
  CanvasTexture,
  SRGBColorSpace
} from 'three';
import { LineMaterial } from 'three/examples/jsm/lines/LineMaterial.js';
import { Line2 } from 'three/examples/jsm/lines/Line2.js';
import { LineGeometry } from 'three/examples/jsm/lines/LineGeometry.js';
import { Engine } from '../engine';
import { ArrowHelper } from './load-helpers/arrowhelper';
import { OVERLAY_LABEL_RENDER_ORDER, arrowHead, overlayModelSize } from './visualization/overlay-style';
import { PerFaceMaterial, extractFaceGeometries } from './per-face-material';
import { FaceNormalStats, faceNormalStatsFromGeometry, faceNormalStatsFromMerged } from './face-normal-stats';
import { resolveDisplayVectors } from '../../utils/local-frame-display.util';
import { colorForSystemId } from '../../utils/coordinate-systems.util';
import { CoordinateSystem, Vec3, deriveAxesFromDefinition } from '../../utils/local-frame.util';

/** Material state a focus dim overwrites, so it can be put back exactly. */
interface FocusDimState {
  transparent: boolean;
  opacity: number;
  depthWrite: boolean;
  renderOrder: number;
}

/** How translucent the design space is drawn. */
export const DESIGN_SPACE_OPACITY = 0.07;

/** How translucent the other input parts and their edges are drawn in Results. */
export const RESULTS_INPUT_OPACITY = 0.12;
const RESULTS_INPUT_EDGE_OPACITY = 0.2;

/**
 * How faint the other parts go while one is being edited. The dithered
 * translucency these replace discarded (1 - opacity) of the pixels and then
 * also blended the rest at `opacity`, so it showed about opacity² of the
 * colour; these keep roughly that density.
 */
const FOCUS_DIM_OPACITY = 0.05;

/**
 * Makes a part material see-through by blending. It does not write depth, so
 * translucent parts never hide what lies behind them, and three draws a
 * double-sided transparent material in two passes (back faces, then front),
 * which keeps its own far side correctly behind its near side. alphaHash is
 * switched off: it drew translucency as per-pixel noise.
 */
export function applyTranslucency(material: any, opacity: number): void {
  if (!material) return;
  const programChanges = material.transparent !== true || material.alphaHash === true;
  material.transparent = true;
  material.opacity = opacity;
  material.depthWrite = false;
  material.alphaHash = false;
  if (programChanges) material.needsUpdate = true;
}

/** Returns a part material to opaque, depth-writing rendering. */
export function clearTranslucency(material: any): void {
  if (!material) return;
  const programChanges = material.transparent !== false || material.alphaHash === true;
  material.transparent = false;
  material.opacity = 1;
  material.depthWrite = true;
  material.alphaHash = false;
  if (programChanges) material.needsUpdate = true;
}

export class Selector {
  public highlightedData: any = undefined;
  private focusDimState = new Map<any, FocusDimState>();
  public highlightedFaces: any[] = [];
  public highlightedWires: any[] = [];
  public selectedFaces: string[] = [];
  public currentInteresected: any;
  public currentInteresectedData: any;
  public clicked = new EventEmitter();
  /** Clicks during an exclusive pick go here instead of `clicked`, so flows
   *  that key off isFacesSelectionEnabled are not hijacked by it. */
  public exclusivePick = new EventEmitter();
  private exclusivePickRunning = false;
  // Colour each face had before the exclusive pick's hover painted it, so
  // unhighlightFace can put back exactly that rather than a flow colour.
  private exclusivePickHoverColors = new Map<string, number>();
  // Colour each face had before preserve picking painted it yellow, so a face
  // clicked again (deselected) goes back to it instead of staying yellow.
  private preservePickColors = new Map<string, number>();
  public moved = new EventEmitter();
  public referenceMoved = new EventEmitter();
  public intersectedEvent = new EventEmitter();
  public rectangleSelected = new EventEmitter<{
    objects: any[];
    pressedKeys: { ctrl: boolean; shift: boolean; alt: boolean };
  }>();
  public isMouseDown = false;
  public isMouseMoveHalted = false;
  public isMouseDownHalted = false;
  public currentPartForFaceSelection: any;
  public allowedFacesToSelect: string[] = [];
  public isFacesSelectionEnabled = false;
  public facesSelectionType?: LoadCasesFlowStep;
  public PartTypeEnum: typeof PartType = PartType;
  public notAllowedPartTypes: any[] = [];

  private raycaster = new Raycaster();

  // Selection rectangle state
  private dragStartPosition: { x: number; y: number } | null = null;
  private dragStartEvent: MouseEvent | null = null;
  private isDragging = false;
  private selectionRectElement: HTMLDivElement | null = null;
  private previewHighlightedObjects: any[] = [];
  private readonly DRAG_THRESHOLD = 5; // pixels

  public CADEditorEnabled = false;

  // Cursor management
  private currentCursor = 'default';

  // Snapshot of the shared face-selection state, taken by beginExclusiveFacePick
  // and restored by endExclusiveFacePick.
  private exclusivePickSnapshot: {
    isFacesSelectionEnabled: boolean;
    allowedFacesToSelect: string[];
    currentPartForFaceSelection: any;
    facesSelectionType: LoadCasesFlowStep | undefined;
  } | null = null;

  constructor(
    public camera: any,
    public canvas: any,
    public groups: any,
    public group: any,
    public objects: any,
    public engine?: Engine
  ) {
    this.addEventListeners();
  }

  // Hover work (raycast, paint) runs at most once per animation frame, for the
  // latest pointer position, and outside Angular: a mousemove does not run
  // change detection for the whole app. Angular is entered only when the
  // hovered part or face changes.
  private pendingMove: MouseEvent | null = null;
  private moveFrame: number | null = null;
  private lastHoverKey: string | null = null;
  private lastPaintKey: string | null = null;

  private readonly mouseDownListener = (e: MouseEvent): void => {
    this.flushPendingHover();
    this.onMouseDown(e);
  };
  private readonly mouseUpListener = (e: MouseEvent): void => {
    this.flushPendingHover();
    this.onMouseUp(e);
  };
  private readonly mouseMoveListener = (e: MouseEvent): void => {
    this.pendingMove = e;
    if (this.moveFrame === null) {
      this.moveFrame = requestAnimationFrame(() => {
        this.moveFrame = null;
        this.flushPendingHover();
      });
    }
  };
  private readonly mouseLeaveListener = (): void => {
    this.cancelPendingHover();
    this.runInZone(() => this.onMouseLeave());
    this.lastHoverKey = null;
    this.lastPaintKey = null;
    this.engine?.requestRender();
  };

  private addEventListeners() {
    this.canvas.addEventListener('mousedown', this.mouseDownListener);
    this.canvas.addEventListener('mouseup', this.mouseUpListener);
    const addHoverListeners = (): void => {
      this.canvas.addEventListener('mousemove', this.mouseMoveListener);
      this.canvas.addEventListener('mouseleave', this.mouseLeaveListener);
    };
    const zone = this.engine?.zone;
    if (zone) {
      zone.runOutsideAngular(addHoverListeners);
    } else {
      addHoverListeners();
    }
  }

  /** Removes the canvas listeners and drops any queued hover. */
  public dispose(): void {
    this.cancelPendingHover();
    this.canvas.removeEventListener('mousedown', this.mouseDownListener);
    this.canvas.removeEventListener('mouseup', this.mouseUpListener);
    this.canvas.removeEventListener('mousemove', this.mouseMoveListener);
    this.canvas.removeEventListener('mouseleave', this.mouseLeaveListener);
  }

  /**
   * Processes the queued mousemove now instead of on the next frame. Clicks
   * call it first so they act on where the pointer is, not where it was a
   * frame ago.
   */
  public flushPendingHover(): void {
    if (this.moveFrame !== null) {
      cancelAnimationFrame(this.moveFrame);
      this.moveFrame = null;
    }
    const e = this.pendingMove;
    this.pendingMove = null;
    if (!e) return;

    const paintKeyBefore = this.lastPaintKey;
    const hoverKeyBefore = this.lastHoverKey;
    this.onMouseMove(e);
    this.lastPaintKey = this.currentPaintKey();
    this.lastHoverKey = this.currentHoverKey();
    if (this.lastHoverKey !== hoverKeyBefore) {
      // A different part or face: repaint, and one change detection pass for
      // anything bound to what is hovered.
      this.engine?.requestRender();
      this.runInZone(() => undefined);
    } else if (this.lastPaintKey !== paintKeyBefore || this.intersectedEvent.observed) {
      // Same part, different object under the pointer (its mesh or its edge
      // lines), or hover listeners (normal picker, open-direction preview)
      // moving 3D helpers with the pointer: repaint only.
      this.engine?.requestRender();
    }
  }

  private cancelPendingHover(): void {
    if (this.moveFrame !== null) {
      cancelAnimationFrame(this.moveFrame);
      this.moveFrame = null;
    }
    this.pendingMove = null;
  }

  /** What is hovered, as the UI sees it: part and face. */
  private currentHoverKey(): string | null {
    const hit = this.currentInteresected;
    if (!hit) return null;
    return `${this.currentInteresectedData?.data?.id ?? ''}|${hit.faceId ?? ''}`;
  }

  /** What is hovered, as the renderer sees it: the object under the pointer and face. */
  private currentPaintKey(): string | null {
    const hit = this.currentInteresected;
    if (!hit) return null;
    return `${hit.object?.name ?? ''}|${hit.faceId ?? ''}`;
  }

  private runInZone(fn: () => void): void {
    const zone = this.engine?.zone;
    if (zone && !NgZone.isInAngularZone()) {
      zone.run(fn);
    } else {
      fn();
    }
  }

  /**
   * The cursor can leave the canvas without a final mousemove over empty
   * space, which would strand the hover paint on the last face or part.
   */
  private onMouseLeave(): void {
    this.setCursor('default');
    // Same gates as onMouseMove: when hover is inactive nothing here was
    // painted by it, and the outline selection may belong to another flow.
    if (this.CADEditorEnabled || this.isMouseDown || this.isMouseMoveHalted) return;
    if (this.engine?.session.isViewOnlyPermission()) return;
    if (!this.clickChannel.observed) return;

    if (this.isFacesSelectionEnabled) {
      this.unhighlightFace();
    } else {
      this.unhighlightPart();
    }

    this.currentInteresected = null;
    this.currentInteresectedData = null;
    this.engine?.outlineEffect?.selection.clear();
    this.engine?.addDrillBitsToOutline();
  }

  private setCursor(cursor: string): void {
    if (this.currentCursor !== cursor) {
      this.currentCursor = cursor;
      this.canvas.style.cursor = cursor;
    }
  }

  public get exclusivePickActive(): boolean {
    return this.exclusivePickRunning;
  }

  private get clickChannel(): EventEmitter<unknown> {
    return this.exclusivePickRunning ? this.exclusivePick : this.clicked;
  }

  private onMouseDown(e: MouseEvent) {
    if (this.CADEditorEnabled) return;
    if (e.altKey) return;

    this.isMouseDown = true;

    // Store drag start position for potential rectangle selection (only for part selection mode)
    if (e.button === 0 && !this.isFacesSelectionEnabled && this.rectangleSelected.observed) {
      this.dragStartPosition = { x: e.clientX, y: e.clientY };
      this.dragStartEvent = e;
      this.isDragging = false;
    }

    if (!this.clickChannel.observed || !this.currentInteresected || this.isMouseDownHalted) return;
    if (e.button !== 0) return;

    // For face selection, we need to get the faceId fresh from the intersection
    // because the intersection object may have been reused by three.js
    let faceId: string | null = null;

    if (this.isFacesSelectionEnabled && this.currentInteresected) {
      // Re-calculate faceId from the current intersection
      faceId = this.getFaceIdFromIntersection(this.currentInteresected);
    } else {
      // Fallback: try from attached property or userData
      faceId = this.currentInteresected.faceId || this.currentInteresected.object?.userData?.faceId || null;
    }

    // Don't emit click immediately - wait for mouseup to distinguish from drag
    // Store the click data for potential emission on mouseup
    if (!this.isFacesSelectionEnabled && this.rectangleSelected.observed) {
      // Will be handled in mouseup
      return;
    }

    this.clickChannel.emit({
      object: this.currentInteresected,
      data: this.currentInteresectedData,
      faceId: faceId, // Include faceId directly for easy access
      pressedKeys: {
        ctrl: e.ctrlKey,
        shift: e.shiftKey,
        alt: e.altKey
      }
    });
  }

  private onMouseUp(e: MouseEvent) {
    if (this.CADEditorEnabled) return;

    const wasDragging = this.isDragging;
    const startPos = this.dragStartPosition;
    const startEvent = this.dragStartEvent;

    // Reset state
    this.isMouseDown = false;
    this.isDragging = false;
    this.dragStartPosition = null;
    this.dragStartEvent = null;
    this.removeSelectionRect();

    // Reset cursor after drag
    if (wasDragging) {
      this.setCursor('default');
    }

    // Handle rectangle selection if was dragging
    if (wasDragging && startPos && this.rectangleSelected.observed) {
      const selectedObjects = this.getObjectsInScreenRect(
        startPos.x,
        startPos.y,
        e.clientX,
        e.clientY
      );

      if (selectedObjects.length > 0) {
        this.rectangleSelected.emit({
          objects: selectedObjects,
          pressedKeys: {
            ctrl: e.ctrlKey,
            shift: e.shiftKey,
            alt: e.altKey
          }
        });
      }
      return;
    }

    // If not dragging and we have a deferred click, emit it now
    if (!wasDragging && startEvent && this.currentInteresected && this.clickChannel.observed && !this.isMouseDownHalted) {
      const faceId = this.currentInteresected.faceId || this.currentInteresected.object?.userData?.faceId || null;
      this.clickChannel.emit({
        object: this.currentInteresected,
        data: this.currentInteresectedData,
        faceId: faceId,
        pressedKeys: {
          ctrl: e.ctrlKey,
          shift: e.shiftKey,
          alt: e.altKey
        }
      });
    }
  }

  private onMouseMove(e: MouseEvent) {
    if (this.CADEditorEnabled) return;

    // Handle selection rectangle dragging
    if (this.isMouseDown && this.dragStartPosition && !this.isFacesSelectionEnabled) {
      const dx = e.clientX - this.dragStartPosition.x;
      const dy = e.clientY - this.dragStartPosition.y;
      const distance = Math.sqrt(dx * dx + dy * dy);

      // Start dragging if moved beyond threshold
      if (distance > this.DRAG_THRESHOLD) {
        if (!this.isDragging) {
          this.isDragging = true;
          this.selectionRectElement = this.createSelectionRect();
          this.setCursor('crosshair');
        }
        this.updateSelectionRect(
          this.dragStartPosition.x,
          this.dragStartPosition.y,
          e.clientX,
          e.clientY
        );
      }
      return;
    }

    if (this.engine?.session.isViewOnlyPermission()) return;
    if (!this.clickChannel.observed || this.isMouseDown || this.isMouseMoveHalted) return;

    const rect = this.canvas.getBoundingClientRect();
    const mouseX = ((e.clientX - rect.x) / this.canvas.clientWidth) * 2 - 1;
    const mouseY = -((e.clientY - rect.y) / this.canvas.clientHeight) * 2 + 1;
    const mousePosition = new Vector2(mouseX, mouseY);

    this.raycaster.firstHitOnly = true;
    this.raycaster.setFromCamera(mousePosition, this.camera);

    const intersectsAllowedObjects = this.objects
      .filter((o: any) => this.notAllowedPartTypes.indexOf(o.type) === -1 && o.group.visible)
      .map((o: any) => o.group);

    const intersects = this.raycaster.intersectObjects(intersectsAllowedObjects) as any[];
    const intersected = intersects[0];

    if (!intersected || intersected.object?.name !== this.currentInteresected?.object?.name) {
      // Only unhighlight parts when NOT in face selection mode
      // Face highlighting uses different approach (color vs emissive)
      if (!this.isFacesSelectionEnabled) {
        this.unhighlightPart();
      }
      this.currentInteresected = null;
      this.currentInteresectedData = null;
      this.engine?.outlineEffect.selection.clear();
      this.engine?.addDrillBitsToOutline();

      // Reset cursor when not hovering over anything
      if (!intersected) {
        this.setCursor('default');
      }
    }

    // Always unhighlight faces when in face selection mode
    if (this.isFacesSelectionEnabled) {
      this.unhighlightFace();
    }

    if (intersected) {
      const data = this.objects.find((o: any) => o.data.id === intersected.object.userData?.['id']);
      if (data) {
        this.intersectedEvent.emit({ data, intersects });

        if (this.isFacesSelectionEnabled) {
          // Get the faceId from the intersection (works with both merged and legacy meshes)
          const faceId = this.getFaceIdFromIntersection(intersected);

          if (faceId && this.allowedFacesToSelect.indexOf(faceId) !== -1) {
            this.currentInteresected = intersected;
            this.currentInteresectedData = data;
            // Store the faceId in the intersection for later use
            intersected.faceId = faceId;
            this.highlightFace(data, intersected);
            this.setCursor('crosshair');
          } else {
            // Hovering over part but not a valid face
            this.setCursor('default');
          }
        } else {
          this.currentInteresected = intersected;
          this.currentInteresectedData = data;

          const obj = this.objects.find((o: any) => data.data.id === o.data.id);
          if (obj) {
            obj.group.children.forEach((child: any) => {
              if (child instanceof Mesh) {
                this.engine?.outlineEffect?.selection.add(child);
              }
            });
          }
          this.highlightPart(data);
          this.setCursor('pointer');
        }
      }
    }
  }

  /**
   * Get the faceId from an intersection with a merged mesh
   * Returns the faceId if it's a merged mesh, or the object's uuid for legacy meshes
   */
  private getFaceIdFromIntersection(intersect: any): string | null {
    const mesh = intersect.object;

    // Check if this is a merged mesh
    if (mesh?.userData?.isMergedMesh) {
      const faceIds = mesh.userData.faceIds as string[];
      const geometry = mesh.geometry;

      if (!geometry || !faceIds) return null;

      // Access faceIndex attribute using bracket notation
      const faceIndexAttr = geometry.attributes['faceIndex'] as BufferAttribute;
      if (!faceIndexAttr) return null;

      // Get the face from the intersection - three.js provides face.a, face.b, face.c as vertex indices
      const face = intersect.face;
      if (!face) return null;

      // Use one of the vertex indices directly (face.a is the first vertex of the hit triangle)
      const vertexIndex = face.a;

      // Get the faceIndex value from the vertex attribute
      const faceIndex = Math.round(faceIndexAttr.getX(vertexIndex));

      // Map to the original faceId
      if (faceIndex >= 0 && faceIndex < faceIds.length) {
        return faceIds[faceIndex];
      }

      return null;
    }

    // Legacy: return the mesh's faceId from userData if available, otherwise uuid
    if (mesh?.userData?.faceId) {
      return mesh.userData.faceId;
    }

    return mesh?.uuid || null;
  }

  public enableFaceSelection(obj: any | any[], step?: LoadCasesFlowStep) {
    this.currentPartForFaceSelection = obj;
    if (Array.isArray(obj)) {
      this.allowedFacesToSelect = obj.flatMap((o: any) => o.faces);
    } else {
      this.allowedFacesToSelect = obj.faces;
    }
    this.isFacesSelectionEnabled = true;
    if (step) {
      this.facesSelectionType = step;
    } else {
      this.facesSelectionType = undefined;
    }
  }

  public disableFaceSelection() {
    this.currentPartForFaceSelection = null;
    this.allowedFacesToSelect = [];
    this.isFacesSelectionEnabled = false;

    // Unhighlight any currently highlighted faces
    this.unhighlightFace();
  }

  /**
   * Starts a pick whose clicks go exclusively to `exclusivePick` instead of
   * the shared `clicked` channel, so the many `clicked` subscribers that key
   * off `isFacesSelectionEnabled` (loads, boundary conditions, etc.) are not
   * hijacked by a pick belonging to a different flow (e.g. UCS).
   *
   * The current face-selection state is snapshotted first and restored by
   * endExclusiveFacePick, because the flow steps that own that state are
   * only hidden while another tab is active, not destroyed - a bare
   * disableFaceSelection() on exit would leave them switched off.
   */
  public beginExclusiveFacePick(obj: any | any[]): void {
    // A pick started on top of a running one keeps the original snapshot;
    // otherwise the end would restore the pick's own state.
    if (!this.exclusivePickRunning) {
      this.exclusivePickSnapshot = {
        isFacesSelectionEnabled: this.isFacesSelectionEnabled,
        allowedFacesToSelect: [...this.allowedFacesToSelect],
        currentPartForFaceSelection: this.currentPartForFaceSelection,
        facesSelectionType: this.facesSelectionType
      };
    }
    this.unhighlightFace();
    this.enableFaceSelection(obj);
    this.exclusivePickRunning = true;
  }

  /**
   * Ends an exclusive pick and restores whatever face-selection state was
   * active before it started. Safe to call when no pick is active.
   */
  public endExclusiveFacePick(): void {
    // Without a running pick there is nothing to restore, and the flow that
    // owns the current face-selection state must keep it.
    if (!this.exclusivePickRunning) return;
    // Clear the hover paint before the snapshot switches the colour rules
    // back to the owning flow's.
    this.unhighlightFace();
    this.exclusivePickRunning = false;
    this.exclusivePickHoverColors.clear();

    const snapshot = this.exclusivePickSnapshot;
    this.exclusivePickSnapshot = null;

    if (snapshot) {
      this.isFacesSelectionEnabled = snapshot.isFacesSelectionEnabled;
      this.allowedFacesToSelect = snapshot.allowedFacesToSelect;
      this.currentPartForFaceSelection = snapshot.currentPartForFaceSelection;
      this.facesSelectionType = snapshot.facesSelectionType;
    } else {
      this.currentPartForFaceSelection = null;
      this.allowedFacesToSelect = [];
      this.isFacesSelectionEnabled = false;
      this.facesSelectionType = undefined;
    }
  }

  /**
   * Get the merged mesh from a part's group, caching the result in userData
   */
  private getMergedMesh(group: any): { mesh: any; perFaceMaterial: PerFaceMaterial; originalColor: number } | null {
    if (!group) return null;

    // Check cache first
    if (group.userData._cachedMergedMesh !== undefined) {
      return group.userData._cachedMergedMesh;
    }

    // Find merged mesh
    const meshObj = group.children?.find((c: any) => c.userData?.isMergedMesh);

    if (meshObj?.userData?.perFaceMaterial) {
      const result = {
        mesh: meshObj,
        perFaceMaterial: meshObj.userData.perFaceMaterial as PerFaceMaterial,
        originalColor: meshObj.userData.originalColor
      };
      group.userData._cachedMergedMesh = result;
      return result;
    }

    group.userData._cachedMergedMesh = null;
    return null;
  }

  public restorePartsColorsToDesignAndNonDesign() {
    for (const obj of this.objects) {
      this.setPartColorByType(obj, obj.type);
    }
  }

  /**
   * Results draws every input part see-through, edges included, so the result
   * inside reads through them; the design space keeps its own faintness.
   * `restorePartsColorsToDesignAndNonDesign` gives them their setup look back.
   */
  public showInputsTranslucent(): void {
    for (const obj of this.objects) {
      this.setPartColorByType(obj, obj.type);
      const opacity = obj.type === PartType.DESIGN ? DESIGN_SPACE_OPACITY : RESULTS_INPUT_OPACITY;

      const merged = this.getMergedMesh(obj.group);
      if (merged) {
        applyTranslucency(merged.perFaceMaterial, opacity);
        merged.mesh.renderOrder = 1;
      } else {
        for (const face of obj.faces) {
          const mesh = this.group.getObjectByName(face) as any;
          if (!mesh?.material) continue;
          applyTranslucency(mesh.material, opacity);
          mesh.renderOrder = 1;
        }
      }

      for (const wire of obj.wires) {
        const line = this.group.getObjectByName(wire) as any;
        if (line?.material) applyTranslucency(line.material, RESULTS_INPUT_EDGE_OPACITY);
      }
    }
  }

  public setPartColorBySubType(data: { faces: string[]; wires: string[]; group?: any }, type: PartSubType) {
    const merged = this.getMergedMesh(data.group);

    if (merged) {
      // Use batched update for merged mesh
      const { perFaceMaterial, originalColor } = merged;
      const color =
        type === PartSubType.BOUNDARY_CONDITION ? PART_BOUNDARY_CONDITION_COLOR : type === PartSubType.LOAD ? PART_LOAD_COLOR : type === PartSubType.BASE ? 0x00bcd4 : originalColor;
      perFaceMaterial.setFacesColor(data.faces, color);
    } else {
      // Legacy: individual mesh objects per face
      for (const face of data.faces) {
        const obj = this.group.getObjectByName(face) as any;
        if (!obj?.material?.color) continue;

        switch (type) {
          case PartSubType.BOUNDARY_CONDITION:
            obj.material.color.set(PART_BOUNDARY_CONDITION_COLOR);
            break;
          case PartSubType.LOAD:
            obj.material.color.set(PART_LOAD_COLOR);
            break;
          case PartSubType.BASE:
            obj.material.color.set(0x00bcd4);
            break;
          default:
            obj.material.color.set(obj.userData?.originalColor || 0xffffff);
            break;
        }
      }
    }

    for (const wire of data.wires) {
      const obj = this.group.getObjectByName(wire) as any;
      if (!obj?.material) continue;

      if (obj.material.color) obj.material.color.set(0x000000);
      this.resetWireWidth(obj.material);
    }
  }

  private partLabelsGroup?: Group;

  /**
   * Floating text label above a part (billboard sprite, Z-up scene). The
   * canvas is rendered at devicePixelRatio and the sprite is rescaled every
   * frame (updatePartLabelScales) so the pill keeps a constant on-screen size
   * across zoom levels, viewport sizes, and DPI. Cleared on every step change.
   */
  public showPartLabel(target: { group?: any }, text: string, colorHex: number) {
    if (!target?.group) return;

    if (!this.partLabelsGroup) {
      this.partLabelsGroup = new Group();
      this.partLabelsGroup.name = 'part-labels';
      this.group.add(this.partLabelsGroup);
    }

    const box = new Box3().setFromObject(target.group);
    if (box.isEmpty()) return;
    const center = box.getCenter(new Vector3());

    // CSS-pixel design of the pill; the backing store is dpr-scaled for crispness
    const cssFontPx = 12;
    const cssHeight = 20;
    const cssPadX = 8;
    const dpr = Math.min(Math.max(window.devicePixelRatio || 1, 1), 3);

    const canvas = document.createElement('canvas');
    const measure = canvas.getContext('2d');
    if (!measure) return;
    measure.font = `600 ${cssFontPx * dpr}px sans-serif`;
    const textWidth = measure.measureText(text).width;
    canvas.width = Math.ceil(textWidth + cssPadX * 2 * dpr);
    canvas.height = Math.ceil(cssHeight * dpr);
    const draw = canvas.getContext('2d');
    if (!draw) return;
    const r = canvas.height / 2;
    draw.fillStyle = '#' + colorHex.toString(16).padStart(6, '0');
    draw.beginPath();
    draw.moveTo(r, 0);
    draw.lineTo(canvas.width - r, 0);
    draw.arc(canvas.width - r, r, r, -Math.PI / 2, Math.PI / 2);
    draw.lineTo(r, canvas.height);
    draw.arc(r, r, r, Math.PI / 2, (3 * Math.PI) / 2);
    draw.closePath();
    draw.fill();
    // Black text on light backgrounds, white on dark
    const lum = 0.299 * ((colorHex >> 16) & 0xff) + 0.587 * ((colorHex >> 8) & 0xff) + 0.114 * (colorHex & 0xff);
    draw.fillStyle = lum > 150 ? '#000000' : '#ffffff';
    draw.font = `600 ${cssFontPx * dpr}px sans-serif`;
    draw.textAlign = 'center';
    draw.textBaseline = 'middle';
    draw.fillText(text, canvas.width / 2, canvas.height / 2 + dpr);

    const texture = new CanvasTexture(canvas);
    // The canvas holds sRGB colours; without this they are read as linear and wash out.
    texture.colorSpace = SRGBColorSpace;
    const material = new SpriteMaterial({ map: texture, depthTest: false, transparent: true });
    const sprite = new Sprite(material);
    sprite.renderOrder = OVERLAY_LABEL_RENDER_ORDER;
    sprite.userData['partLabel'] = {
      cssHeight,
      aspect: canvas.width / canvas.height,
      anchor: new Vector3(center.x, center.y, box.max.z),
    };
    this.partLabelsGroup.add(sprite);
    this.updatePartLabelScales();
  }

  /** Keep labels a constant on-screen size; called from the engine render loop. */
  public updatePartLabelScales() {
    if (!this.partLabelsGroup?.children.length) return;
    const camera = this.camera;
    const clientHeight = this.canvas?.clientHeight;
    if (!camera || camera.top === undefined || !clientHeight) return;
    // Orthographic camera: world units rendered per CSS pixel
    const worldPerCssPx = (camera.top - camera.bottom) / (clientHeight * (camera.zoom || 1));
    for (const sprite of this.partLabelsGroup.children as Sprite[]) {
      const info = sprite.userData['partLabel'];
      if (!info) continue;
      const h = info.cssHeight * worldPerCssPx;
      sprite.scale.set(h * info.aspect, h, 1);
      sprite.position.set(info.anchor.x, info.anchor.y, info.anchor.z + h);
    }
  }

  /**
   * Direction arrow over a set of parts (support-group excitation direction).
   * Anchored at the union bounding-box center, always rendered on top; lives
   * in the same layer as part labels and is cleared with them.
   */
  public showPartGroupArrow(targets: { group?: any }[], direction: [number, number, number], colorHex: number) {
    const [dx, dy, dz] = direction;
    const magnitude = Math.sqrt(dx * dx + dy * dy + dz * dz);
    // A half-typed direction (empty input => NaN) must not poison the scene
    if (!Number.isFinite(magnitude) || magnitude === 0 || targets.length === 0) return;

    if (!this.partLabelsGroup) {
      this.partLabelsGroup = new Group();
      this.partLabelsGroup.name = 'part-labels';
      this.group.add(this.partLabelsGroup);
    }

    const box = new Box3();
    for (const target of targets) {
      if (target?.group) box.expandByObject(target.group);
    }
    if (box.isEmpty()) return;
    const size = box.getSize(new Vector3());
    const center = box.getCenter(new Vector3());
    const length = size.length() * 1.1;

    const dir = new Vector3(dx / magnitude, dy / magnitude, dz / magnitude);
    // Start at the part surface along the direction so the shaft reads as attached
    const origin = center.clone().addScaledVector(dir, size.length() * 0.35);
    // ArrowHelper draws half its length from its origin, so it gets twice the drawn length.
    const head = arrowHead(length, overlayModelSize(this.engine?.objectInfo?.size ?? size));
    const arrow = new ArrowHelper('line', dir, origin, length * 2, colorHex, head.length, head.radius);
    arrow.traverse((child) => child.layers.set(0));
    this.partLabelsGroup.add(arrow);
  }

  /**
   * Marks the parts being edited by fading every other part back and naming
   * each of them. Category colour cannot carry this on its own: every part
   * with a boundary condition is already the same orange, and every part with
   * a load the same green, so with more than one there is nothing to tell
   * them apart. A group names each of its parts rather than itself, which is
   * what tells you which parts are in it.
   */
  public focusParts(targets: SelectorObject[], colorHex: number): void {
    this.clearPartFocus();
    this.clearPartLabels();

    const focused = new Set((targets ?? []).filter((target) => target?.group));
    if (focused.size === 0) return;

    for (const obj of this.objects) {
      if (!focused.has(obj)) this.dimPart(obj);
    }

    for (const target of focused) {
      const name = target.data?.name;
      if (name) this.showPartLabel(target, name, colorHex);
    }
  }

  /** Puts every faded part back to the exact state it was dimmed from. */
  public clearPartFocus(): void {
    for (const [mesh, state] of this.focusDimState) {
      const material = mesh.material;
      if (material) {
        material.transparent = state.transparent;
        material.opacity = state.opacity;
        material.depthWrite = state.depthWrite;
        // transparent changes the compiled program.
        material.needsUpdate = true;
      }
      mesh.renderOrder = state.renderOrder;
    }
    this.focusDimState.clear();
  }

  private dimPart(obj: SelectorObject): void {
    const merged = this.getMergedMesh(obj.group);
    if (merged) {
      this.dimMesh(merged.mesh);
      return;
    }

    for (const face of obj.faces ?? []) {
      const faceObj = this.group.getObjectByName(face);
      if (faceObj) this.dimMesh(faceObj);
    }
  }

  private dimMesh(mesh: any): void {
    const material = mesh?.material;
    // Recorded before anything is touched, and only once: focusing twice
    // without a clear would otherwise snapshot the already-dimmed state and
    // restore the parts to a ghost.
    if (!material || this.focusDimState.has(mesh)) return;

    this.focusDimState.set(mesh, {
      transparent: material.transparent,
      opacity: material.opacity,
      depthWrite: material.depthWrite ?? true,
      renderOrder: mesh.renderOrder
    });

    // Blended like the design space (see applyTranslucency).
    applyTranslucency(material, Math.min(material.opacity ?? 1, FOCUS_DIM_OPACITY));
    mesh.renderOrder = 1;
  }

  /** Puts a part's edge lines back to their normal (unselected) weight. */
  private resetWireWidth(material: any): void {
    if (material instanceof LineMaterial && this.engine) {
      this.engine.setWireSelected(material, false);
    } else if (material?.linewidth !== undefined) {
      material.linewidth = 1;
    }
  }

  public clearPartLabels() {
    if (!this.partLabelsGroup) return;
    // Children are a mix of label Sprites and direction ArrowHelpers —
    // dispose defensively per type so one child can't abort the sweep.
    for (const child of [...this.partLabelsGroup.children] as any[]) {
      this.partLabelsGroup.remove(child);
      if (typeof child.dispose === 'function') {
        child.dispose();
      } else {
        child.material?.map?.dispose?.();
        child.material?.dispose?.();
      }
    }
  }

  /** Paint a part with an arbitrary color (e.g. per-support-group colors). */
  public setPartColorCustom(data: { faces: string[]; wires: string[]; group?: any }, color: number) {
    const merged = this.getMergedMesh(data.group);

    if (merged) {
      merged.perFaceMaterial.setFacesColor(data.faces, color);
    } else {
      for (const face of data.faces) {
        const obj = this.group.getObjectByName(face) as any;
        if (!obj?.material?.color) continue;
        obj.material.color.set(color);
      }
    }

    for (const wire of data.wires) {
      const obj = this.group.getObjectByName(wire) as any;
      if (!obj?.material) continue;

      if (obj.material.color) obj.material.color.set(0x000000);
      this.resetWireWidth(obj.material);
    }
  }

  public setPartColorByType(data: { faces: string[]; wires: string[]; group?: any }, type: PartType) {
    this.highlightedData = undefined;
    this.highlightedFaces = [];
    this.highlightedWires = [];

    const designSettings = {
      transparent: true,
      opacity: DESIGN_SPACE_OPACITY,
      color: 0xff964f,
      side: DoubleSide,
      renderOrder: 1
    };

    const nonDesignSettings = {
      transparent: false,
      opacity: 1,
      color: 0xa7c7e7,
      renderOrder: 0
    };

    const resetSettings = {
      side: DoubleSide,
      transparent: false,
      opacity: 1,
      renderOrder: 0
    };

    const applySettings = (obj: any, settings: any) => {
      if (!obj?.material) return;
      if (obj.material.color) obj.material.color.set(settings.color);
      if (obj.material.side !== undefined) obj.material.side = settings.side ?? obj.material.side;
      if (settings.transparent) {
        applyTranslucency(obj.material, settings.opacity);
      } else {
        clearTranslucency(obj.material);
      }
      obj.renderOrder = settings.renderOrder;
    };

    const merged = this.getMergedMesh(data.group);

    if (merged) {
      const { mesh, perFaceMaterial, originalColor } = merged;

      // Apply material-level settings for the entire mesh
      if (type === PartType.DESIGN) {
        applyTranslucency(perFaceMaterial, DESIGN_SPACE_OPACITY);
      } else {
        clearTranslucency(perFaceMaterial);
      }
      mesh.renderOrder = type === PartType.DESIGN ? 1 : 0;

      // Set per-face colors using batched update
      const faceColor = type === PartType.DESIGN ? 0xff964f : type === PartType.NONDESIGN ? 0xa7c7e7 : originalColor;

      perFaceMaterial.beginBatchUpdate();
      for (const faceId of data.faces) {
        perFaceMaterial.setFaceState(faceId, faceColor, 0);
      }
      perFaceMaterial.endBatchUpdate();
    } else {
      // Legacy: individual mesh objects per face
      for (const face of data.faces) {
        const obj = this.group.getObjectByName(face) as any;
        if (!obj?.material) continue;

        if (obj.material.emissive) obj.material.emissive.set(0x000000);

        switch (type) {
          case PartType.DESIGN:
            applySettings(obj, designSettings);
            break;
          case PartType.NONDESIGN:
            applySettings(obj, nonDesignSettings);
            break;
          default:
            applySettings(obj, { ...resetSettings, color: obj.userData?.originalColor || 0xffffff });
            break;
        }
      }
    }

    for (const wire of data.wires) {
      const obj = this.group.getObjectByName(wire) as any;
      if (!obj?.material) continue;

      if (obj.material.color) obj.material.color.set(0x000000);
      if (obj.material.transparent) clearTranslucency(obj.material);
      this.resetWireWidth(obj.material);
    }
  }

  public findObjectByUserData(obj: Object3D, key: string, value: any): Object3D | null {
    let foundObject: Object3D | null = null;

    obj.traverse((child) => {
      if (child.userData && child.userData[key] === value) {
        foundObject = child;
        return;
      }
    });

    return foundObject;
  }

  /**
   * Get geometries for specific face IDs from a part
   * Works with both merged meshes (new) and legacy individual face meshes
   */
  public getFaceGeometries(partData: any, faceIds: string[]): BufferGeometry[] {
    const merged = this.getMergedMesh(partData.group);

    if (merged && merged.mesh.geometry) {
      // Merged mesh: extract geometries from merged geometry
      const faceIdToIndex = merged.mesh.userData.faceIdToIndex as Map<string, number>;
      if (faceIdToIndex) {
        return extractFaceGeometries(merged.mesh.geometry, faceIdToIndex, faceIds);
      }
    }

    // Legacy: find individual face meshes
    const geometries: BufferGeometry[] = [];
    for (const faceId of faceIds) {
      const meshObj = this.findObjectByUserData(partData.group, 'faceId', faceId) as Mesh | null;
      if (meshObj && meshObj.geometry) {
        geometries.push(meshObj.geometry);
      }
    }
    return geometries;
  }

  /**
   * Normal spread for every face of a part. A rotation axis has to run along
   * the face it is applied to, and that is not something the user can see by
   * looking at the model.
   */
  public getFaceNormalStats(partData: any): FaceNormalStats[] {
    const merged = this.getMergedMesh(partData.group);

    if (merged && merged.mesh.geometry) {
      const faceIdToIndex = merged.mesh.userData.faceIdToIndex as Map<string, number>;
      if (faceIdToIndex) {
        return faceNormalStatsFromMerged(merged.mesh.geometry, faceIdToIndex);
      }
    }

    const stats: FaceNormalStats[] = [];
    for (const faceId of partData.faces ?? []) {
      const meshObj = this.findObjectByUserData(partData.group, 'faceId', faceId) as Mesh | null;
      if (meshObj?.geometry) {
        stats.push(faceNormalStatsFromGeometry(faceId, meshObj.geometry as BufferGeometry));
      }
    }
    return stats;
  }

  public updateFaces(faceIds: string[]) {
    this.restoreDeselectedPreserveFaces(faceIds);
    if (this.isFacesSelectionEnabled && this.facesSelectionType === LoadCasesFlowStep.PRESERVES) {
      for (const faceId of faceIds) {
        if (this.preservePickColors.has(faceId)) continue;
        const color = this.faceColor(faceId);
        if (color !== null) this.preservePickColors.set(faceId, color);
      }
    }
    this.selectedFaces = [...faceIds];

    // Update faces for all objects
    for (const obj of this.objects) {
      const merged = this.getMergedMesh(obj.group);
      if (merged) {
        // Merged mesh: use PerFaceMaterial with batch update
        const matchingFaces = faceIds.filter((faceId) => obj.faces.includes(faceId));
        if (matchingFaces.length > 0) {
          merged.perFaceMaterial.setFacesColor(matchingFaces, 0xffd700);
        }
      } else {
        // Legacy: update individual mesh objects
        for (const faceId of faceIds) {
          if (!obj.faces.includes(faceId)) continue;
          const meshObject = this.findObjectByUserData(obj.group, 'faceId', faceId);
          if (meshObject && (meshObject as any).material?.color) {
            (meshObject as any).material.color.set(0xffd700);
          }
        }
      }
    }
  }

  /** Preserve picking: faces no longer in `keep` get back the colour they had before. */
  private restoreDeselectedPreserveFaces(keep: string[]): void {
    for (const [faceId, color] of this.preservePickColors) {
      if (keep.includes(faceId)) continue;
      this.setFaceColor(faceId, color);
      this.preservePickColors.delete(faceId);
    }
  }

  private faceColor(faceId: string): number | null {
    for (const obj of this.objects) {
      if (!obj.faces.includes(faceId)) continue;
      const merged = this.getMergedMesh(obj.group);
      if (merged) return merged.perFaceMaterial.getFaceColor(faceId)?.getHex() ?? null;
      const meshObject = this.findObjectByUserData(obj.group, 'faceId', faceId) as any;
      return meshObject?.material?.color?.getHex() ?? null;
    }
    return null;
  }

  private setFaceColor(faceId: string, color: number): void {
    for (const obj of this.objects) {
      if (!obj.faces.includes(faceId)) continue;
      const merged = this.getMergedMesh(obj.group);
      if (merged) {
        merged.perFaceMaterial.setFaceColor(faceId, color);
      } else {
        (this.findObjectByUserData(obj.group, 'faceId', faceId) as any)?.material?.color?.set(color);
      }
      return;
    }
  }

  /**
   * Colour a hovered face returns to once the cursor leaves it.
   *
   * A load-case step repaints the whole part in its own colour before the
   * pick begins, so there a face goes back to that flow colour. An exclusive
   * pick makes no such assumption about the part's paint and puts back the
   * colour recorded when the hover started.
   */
  private hoverRestoreColor(faceId: string, originalColor: number): number {
    if (this.exclusivePickRunning) {
      return this.exclusivePickHoverColors.get(faceId) ?? originalColor;
    }

    if (this.engine?.session.project.projectType === 'soft_jaws') return originalColor;

    return this.facesSelectionType === LoadCasesFlowStep.LOADS ? 0x32cd32 : 0xff4500;
  }

  private rememberHoverColor(faceId: string, color: number | undefined): void {
    if (!this.exclusivePickRunning || color === undefined || this.exclusivePickHoverColors.has(faceId)) return;
    this.exclusivePickHoverColors.set(faceId, color);
  }

  public highlightFace(data: any, intersect: any) {
    // Preserve-pick mode: cursor still tracks the face (so click resolution
    // works) but no hover paint — only the click→yellow state is visible.
    // This keeps the manual preserve workflow free of stale fill colors
    // across the rest of the part.
    if (this.facesSelectionType === LoadCasesFlowStep.PRESERVES) {
      return;
    }

    const faceId = intersect.faceId;
    const merged = this.getMergedMesh(data.group);

    if (merged && faceId) {
      this.rememberHoverColor(faceId, merged.perFaceMaterial.getFaceColor(faceId)?.getHex());
      merged.perFaceMaterial.setFaceColor(faceId, 0x007bff);
      this.highlightedFaces.push(faceId);
      this.highlightedData = data;
    } else if (intersect.object?.uuid) {
      // Legacy: use the mesh object
      const obj = this.group.getObjectByName(intersect.object.uuid) as any;
      if (obj?.material?.color) {
        this.rememberHoverColor(intersect.object.uuid, obj.material.color.getHex());
        obj.material.color.set(0x007bff);
        this.highlightedFaces.push(intersect.object.uuid);
        this.highlightedData = data;
      }
    }
  }

  public unhighlightFace(force?: boolean) {
    // Preserve-pick mode never paints on hover (see highlightFace), so
    // there's nothing to restore here either. Just clear our internal
    // tracking and re-apply the selected-faces highlight so the click→
    // yellow state stays consistent.
    if (this.facesSelectionType === LoadCasesFlowStep.PRESERVES) {
      this.highlightedFaces = [];
      this.highlightedData = undefined;
      this.highlightedWires = [];
      this.updateFaces(this.selectedFaces);
      return;
    }

    // Store references before clearing to avoid race conditions
    const facesToUnhighlight = [...this.highlightedFaces];
    const dataToUse = this.highlightedData;

    if (facesToUnhighlight.length === 0 || !dataToUse) {
      return;
    }

    // Clear immediately to prevent race conditions with fast mouse movement
    this.highlightedFaces = [];
    this.highlightedData = undefined;
    this.highlightedWires = [];

    const merged = this.getMergedMesh(dataToUse.group);

    if (merged) {
      const { perFaceMaterial, originalColor } = merged;

      // Filter out selected faces and batch update
      const facesToRestore = facesToUnhighlight.filter((faceId) => !this.selectedFaces.includes(faceId));
      if (facesToRestore.length > 0) {
        perFaceMaterial.beginBatchUpdate();
        for (const faceId of facesToRestore) {
          perFaceMaterial.setFaceColor(faceId, this.hoverRestoreColor(faceId, originalColor));
        }
        perFaceMaterial.endBatchUpdate();
      }
    } else {
      // Legacy: update individual mesh objects
      for (const face of facesToUnhighlight) {
        const obj = this.group.getObjectByName(face) as any;
        if (!obj?.material?.color) continue;

        const faceIndex = this.selectedFaces.indexOf(obj.userData?.faceId);
        if (faceIndex >= 0) continue;

        obj.material.color.set(this.hoverRestoreColor(face, obj.userData?.originalColor || 0xffffff));
      }
    }

    for (const faceId of facesToUnhighlight) this.exclusivePickHoverColors.delete(faceId);
    this.updateFaces(this.selectedFaces);
  }

  public highlightPart(data: any) {
    if (data.type === PartType.DESIGN || data.type === PartType.NONDESIGN) return;

    const merged = this.getMergedMesh(data.group);

    if (merged) {
      // Use batched emissive update for merged mesh
      merged.perFaceMaterial.setFacesEmissive(data.faces, 1.0);
      this.highlightedFaces.push(...data.faces);
      this.highlightedData = data;
    } else {
      // Legacy: individual mesh objects per face
      for (const face of data.faces) {
        const obj = this.group.getObjectByName(face) as any;
        if (!obj?.material?.emissive) continue;
        obj.material.emissive.set(0x999999);
        this.highlightedFaces.push(face);
        this.highlightedData = data;
      }
    }

    for (const wire of data.wires) {
      const obj = this.group.getObjectByName(wire) as any;
      if (!obj?.material?.color) continue;
      obj.material.color.set(0x333333);
      this.highlightedWires.push(wire);
    }
  }

  public unhighlightPart() {
    // Store references before clearing to avoid race conditions
    const facesToUnhighlight = [...this.highlightedFaces];
    const wiresToUnhighlight = [...this.highlightedWires];
    const dataToUse = this.highlightedData;

    if (facesToUnhighlight.length === 0 || !dataToUse) {
      return;
    }

    // Clear immediately to prevent race conditions
    this.highlightedData = undefined;
    this.highlightedFaces = [];
    this.highlightedWires = [];

    const merged = this.getMergedMesh(dataToUse.group);

    if (merged) {
      // Use batched emissive reset for merged mesh
      merged.perFaceMaterial.setFacesEmissive(facesToUnhighlight, 0);
    } else {
      // Legacy: individual mesh objects per face
      for (const face of facesToUnhighlight) {
        const obj = this.group.getObjectByName(face) as any;
        if (obj?.material?.emissive) obj.material.emissive.set(0x000000);
      }
    }

    for (const wire of wiresToUnhighlight) {
      const obj = this.group.getObjectByName(wire) as any;
      if (obj?.material?.color) obj.material.color.set(0x000000);
    }
  }

  public getPartType(item: any): PartType {
    return item.type;
  }

  public setPartType(item: any, type: PartType) {
    item.type = type;
    this.setPartColorByType(item, type);
    this.engine?.sharpEdges?.refresh();
    this.engine?.designOverlap?.refresh();
    this.engine?.thinRegions?.refresh();
  }

  // These annotation methods are no-ops - annotations are currently disabled
  public showLoadAnnotation(_group: Group) {}
  public hideLoadAnnotation(_group: Group) {}
  /**
   * A three-axis cross at the constrained part: locked axes solid, free axes
   * faint, oriented by the BC's frame. Boundary conditions had no directional
   * visual before this, in any frame.
   *
   * Resolution is entirely the caller's responsibility - this never infers
   * which BC a group corresponds to (there is no "current load case" concept
   * for the load-case editing flow, only for the results view, and guessing
   * wrong would show the wrong load case's locked axes). Without a bc it
   * clears any triad this group previously drew and draws nothing.
   *
   * The key is stashed on the group (mirrors loadFrameTriadKey below) so
   * hideBoundaryConditionAnnotation can clear it without needing a bc at all,
   * and so redrawing the same group with a different bc - or a bc whose
   * frame just went degenerate - clears the OLD key instead of orphaning it.
   */
  public showBoundaryConditionAnnotation(
    group: Group,
    bc?: BoundaryConditionItem,
    systems?: CoordinateSystem[]
  ): void {
    const previousKey = group.userData['bcFrameTriadKey'] as string | undefined;
    if (previousKey) this.engine?.clearFrameTriad(previousKey);
    delete group.userData['bcFrameTriadKey'];
    if (!bc) return;

    const key = `bc-${bc.id}`;
    const resolvedSystems = systems ?? this.engine?.session.project?.coordinateSystems ?? [];
    const globalAxes = { ex: [1, 0, 0] as Vec3, ey: [0, 1, 0] as Vec3, ez: [0, 0, 1] as Vec3 };
    const system = resolvedSystems.find((candidate) => candidate.id === bc.coordinateSystemId);
    // A dangling or degenerate frame draws on the global axes, matching what
    // the emitter would refuse to run and what the badge shows.
    const axes = (system && deriveAxesFromDefinition(system.definition)) || globalAxes;

    // Markers live in model space; the bounding box comes back in world space.
    const centre = new Box3().setFromObject(group).getCenter(new Vector3());
    if (this.engine?.group) this.engine.group.worldToLocal(centre);
    const locked = [bc.fixedX, bc.fixedY, bc.fixedZ];
    this.engine?.drawConstraintCross(
      key,
      centre,
      axes,
      locked,
      axes === globalAxes ? null : colorForSystemId(resolvedSystems, bc.coordinateSystemId ?? null)
    );
    group.userData['bcFrameTriadKey'] = key;
  }

  public hideBoundaryConditionAnnotation(group: Group, bc?: BoundaryConditionItem): void {
    const key = (group.userData['bcFrameTriadKey'] as string | undefined) ?? (bc ? `bc-${bc.id}` : undefined);
    if (!key) return;
    this.engine?.clearFrameTriad(key);
    delete group.userData['bcFrameTriadKey'];
  }

  public showForceAndMomentWithManipulator(group: Group) {
    const { partArrow, partMoment, partSphere, partLink } = this.getForceAndMomentWithManipulator(group);
    partArrow?.children.forEach((o) => o.layers.set(0));
    partMoment?.children.forEach((o) => o.layers.set(0));
    partSphere?.layers.set(0);
    // In remote-mass mode the black part-center link stays hidden no matter which
    // code path re-shows the manipulator (drag ticks re-show it every frame)
    partLink?.layers.set(group.userData['suppressPartLink'] ? 1 : 0);
  }

  public setForceAndMomentWithManipulator(group: Group, load: LoadItem) {
    const { partArrow, partMoment, partSphere, partLink } = this.getForceAndMomentWithManipulator(group);
    const remotePoint = new Vector3(...load.remotePoint);
    // Drawn in global. The stored components may be authored in a local frame,
    // and an arrow along the stored axes would contradict the solver.
    const { force: forceComponent, moment: momentComponent } = resolveDisplayVectors(
      load,
      this.engine?.session.project?.coordinateSystems ?? []
    );

    partArrow?.position.copy(remotePoint);
    partMoment?.position.copy(remotePoint);
    partSphere?.position.copy(remotePoint);
    const geometry = new LineGeometry();
    const center = new Box3().setFromObject(group).getCenter(new Vector3());
    center.applyMatrix4(group.matrixWorld.clone().invert());
    group.localToWorld(center);
    // The link sits in the helper holder with the remote point.
    if (partLink?.parent) {
      partLink.parent.updateWorldMatrix(true, false);
      partLink.parent.worldToLocal(center);
    }
    geometry.setPositions([center.x, center.y, center.z, remotePoint.x, remotePoint.y, remotePoint.z]);
    partLink?.geometry.copy(geometry); //.setFromPoints([group.position.clone().addScalar(100), remotePoint]);
    partLink?.computeLineDistances();
    partLink?.scale.set(1, 1, 1);

    partArrow?.setDirection(forceComponent);
    partMoment?.setDirection(momentComponent);

    const systems = this.engine?.session.project?.coordinateSystems ?? [];
    const frameColor = colorForSystemId(systems, load.coordinateSystemId ?? null);
    // colorForSystemId returns non-null iff the id resolves, so this find is
    // expected to hit whenever frameColor is set - branched rather than
    // asserted so that coupling cannot silently regress into a crash.
    const system = frameColor ? systems.find((s) => s.id === load.coordinateSystemId) : undefined;
    const axes = system ? deriveAxesFromDefinition(system.definition) : null;
    // Triad key is deterministic per load regardless of framing, so
    // hideForceAndMomentWithManipulator can always find and clear it later.
    const triadKey = `load-${load.id}`;
    group.userData['loadFrameTriadKey'] = triadKey;
    if (frameColor && axes) {
      partArrow?.setColor(frameColor);
      // The frame's rotation at the load's own application point.
      this.engine?.drawFrameTriad(triadKey, remotePoint, axes, frameColor);
    } else {
      // Global frame, or a resolved-but-degenerate frame: restore the
      // default force-arrow colour in case a previously assigned frame
      // tinted it, and don't draw a triad for a direction the arrow isn't
      // actually following.
      partArrow?.setColor('#0000ff');
      this.engine?.clearFrameTriad(triadKey);
    }

    partArrow?.children.forEach((o) => o.layers.set(forceComponent.equals(new Vector3(0, 0, 0)) ? 1 : 0));
    partMoment?.children.forEach((o) => o.layers.set(momentComponent.equals(new Vector3(0, 0, 0)) ? 1 : 0));

    if (load.type === LoadType.ACCELERATION) {
      partSphere?.layers.set(1);
      partArrow?.children.forEach((o) => o.layers.set(1));
      partMoment?.children.forEach((o) => o.layers.set(1));
    }
  }

  public dragForceAndMomentWithManipulator(group: Group, enabled: boolean) {
    const { partSphere } = this.getForceAndMomentWithManipulator(group);
    if (partSphere?.userData['dragControl']) {
      partSphere.userData['dragControl'].enabled = enabled;
    }
  }

  public hideForceAndMomentArrow(group?: Group) {
    if (group) {
      const { partArrow, partMoment } = this.getForceAndMomentWithManipulator(group);
      partArrow?.children.forEach((o) => o.layers.set(1));
      partMoment?.children.forEach((o) => o.layers.set(1));
    }
  }

  public hideForceAndMomentWithManipulator(group?: Group) {
    if (group) {
      const { partArrow, partMoment, partSphere, partLink } = this.getForceAndMomentWithManipulator(group);
      partArrow?.children.forEach((o) => o.layers.set(1));
      partMoment?.children.forEach((o) => o.layers.set(1));
      partSphere?.layers.set(1);
      partLink?.layers.set(1);
      this.hideRemoteMassMarkers(group);
      // setForceAndMomentWithManipulator draws a load's triad outside the
      // load-case-highlight rebuild (ordinary editing), so hiding the
      // manipulator is that triad's other exit and clears the same key.
      const triadKey = group.userData['loadFrameTriadKey'] as string | undefined;
      if (triadKey) this.engine?.clearFrameTriad(triadKey);
    } else {
      for (const obj of this.objects) {
        this.hideForceAndMomentWithManipulator(obj.group);
      }
    }
  }

  public getForceAndMomentWithManipulator(group: Group) {
    const partArrow = group.userData['part_arrow'] as ArrowHelper;
    const partMoment = group.userData['part_moment'] as ArrowHelper;
    const partSphere = group.userData['part_sphere'];
    const partLink = group.userData['part_link_line'] as Line2;
    return { partArrow, partMoment, partSphere, partLink };
  }

  // ============ Remote-Mass Reference Markers ============
  // "Remote Mass" / "Reference Point" labels, the draggable reference sphere and
  // the offset connector, shown for vibration-family loads in remotePoint mode.

  public getRemoteMassMarkers(group: Group) {
    const referenceSphere = group.userData['part_reference_sphere'] as Mesh | undefined;
    const referenceLink = group.userData['part_reference_link_line'] as Line2 | undefined;
    const massAnnotation = group.userData['part_sphere_annotation'] as Object3D | undefined;
    const referenceAnnotation = group.userData['part_reference_annotation'] as Object3D | undefined;
    return { referenceSphere, referenceLink, massAnnotation, referenceAnnotation };
  }

  /**
   * One-stop update: positions the markers and decides visibility. The reference
   * sphere/labels/connector only show while the two points actually differ —
   * coincident points would stack two draggable spheres (a single drag would then
   * grab both), and a zero offset has nothing to visualize anyway.
   */
  public syncRemoteMassMarkers(group: Group, load: LoadItem, enabled: boolean) {
    this.setRemoteMassMarkers(group, load);
    const remoteMassMode = enabled && load.massType === 'remotePoint';
    const pointsDiffer = deriveMassType(load.remotePoint, effectiveReferencePoint(load)) === 'remotePoint';
    if (remoteMassMode && pointsDiffer) {
      this.showRemoteMassMarkers(group);
    } else {
      this.hideRemoteMassMarkers(group);
    }
    // In remote-mass mode the amber offset connector replaces the black
    // part-center link — showing both just clutters the view. The flag makes
    // showForceAndMomentWithManipulator keep it hidden on every re-show path.
    group.userData['suppressPartLink'] = remoteMassMode;
    if (remoteMassMode) {
      const { partLink } = this.getForceAndMomentWithManipulator(group);
      partLink?.layers.set(1);
    }
  }

  /** Positions the reference sphere at the effective reference point and redraws the offset connector. */
  public setRemoteMassMarkers(group: Group, load: LoadItem) {
    const { referenceSphere, referenceLink } = this.getRemoteMassMarkers(group);
    const reference = new Vector3(...effectiveReferencePoint(load));
    const remote = new Vector3(...load.remotePoint);
    referenceSphere?.position.copy(reference);
    if (referenceLink) {
      const geometry = new LineGeometry();
      geometry.setPositions([reference.x, reference.y, reference.z, remote.x, remote.y, remote.z]);
      referenceLink.geometry.copy(geometry);
      referenceLink.computeLineDistances();
      referenceLink.scale.set(1, 1, 1);
    }
  }

  public showRemoteMassMarkers(group: Group) {
    const { referenceSphere, referenceLink, massAnnotation, referenceAnnotation } = this.getRemoteMassMarkers(group);
    referenceSphere?.layers.set(0);
    referenceLink?.layers.set(0);
    if (massAnnotation) massAnnotation.visible = true;
    if (referenceAnnotation) referenceAnnotation.visible = true;
    if (referenceSphere?.userData['dragControl']) referenceSphere.userData['dragControl'].enabled = true;
  }

  public hideRemoteMassMarkers(group?: Group) {
    if (group) {
      const { referenceSphere, referenceLink, massAnnotation, referenceAnnotation } = this.getRemoteMassMarkers(group);
      referenceSphere?.layers.set(1);
      referenceLink?.layers.set(1);
      if (massAnnotation) massAnnotation.visible = false;
      if (referenceAnnotation) referenceAnnotation.visible = false;
      if (referenceSphere?.userData['dragControl']) referenceSphere.userData['dragControl'].enabled = false;
    } else {
      for (const obj of this.objects) {
        this.hideRemoteMassMarkers(obj.group);
      }
    }
  }

  // ============ Selection Rectangle Methods ============

  private createSelectionRect(): HTMLDivElement {
    const rect = document.createElement('div');
    rect.style.position = 'fixed';
    rect.style.pointerEvents = 'none';
    rect.style.zIndex = '10000';
    rect.style.border = '2px solid rgba(160, 255, 0, 0.8)'; // hero-green
    rect.style.backgroundColor = 'rgba(160, 255, 0, 0.1)'; // hero-green with subtle opacity

    document.body.appendChild(rect);
    return rect;
  }

  private updateSelectionRect(startX: number, startY: number, currentX: number, currentY: number): void {
    if (!this.selectionRectElement) return;

    const left = Math.min(startX, currentX);
    const top = Math.min(startY, currentY);
    const width = Math.abs(currentX - startX);
    const height = Math.abs(currentY - startY);

    this.selectionRectElement.style.left = `${left}px`;
    this.selectionRectElement.style.top = `${top}px`;
    this.selectionRectElement.style.width = `${width}px`;
    this.selectionRectElement.style.height = `${height}px`;

    // Update preview highlights to show which objects will be selected
    const objectsInRect = this.getObjectsInScreenRect(startX, startY, currentX, currentY);
    this.updatePreviewHighlights(objectsInRect);
  }

  private removeSelectionRect(): void {
    // Clear preview highlights
    this.clearPreviewHighlights();

    if (this.selectionRectElement) {
      this.selectionRectElement.remove();
      this.selectionRectElement = null;
    }
  }

  private updatePreviewHighlights(objectsInRect: any[]): void {
    // Find objects to unhighlight (were highlighted but no longer in rect)
    const objectsToUnhighlight = this.previewHighlightedObjects.filter(
      (obj) => !objectsInRect.includes(obj)
    );

    // Find objects to highlight (in rect but not yet highlighted)
    const objectsToHighlight = objectsInRect.filter(
      (obj) => !this.previewHighlightedObjects.includes(obj)
    );

    // Unhighlight objects no longer in selection
    for (const obj of objectsToUnhighlight) {
      this.unhighlightPreviewObject(obj);
    }

    // Highlight new objects in selection
    for (const obj of objectsToHighlight) {
      this.highlightPreviewObject(obj);
    }

    // Update tracked list
    this.previewHighlightedObjects = [...objectsInRect];
  }

  private highlightPreviewObject(obj: any): void {
    if (!obj?.group) return;

    // Add to outline effect for visual feedback
    obj.group.children.forEach((child: any) => {
      if (child instanceof Mesh) {
        this.engine?.outlineEffect?.selection.add(child);
      }
    });

    // Also highlight using emissive for merged meshes
    const merged = this.getMergedMesh(obj.group);
    if (merged) {
      merged.perFaceMaterial.setFacesEmissive(obj.faces, 0.5);
    }
  }

  private unhighlightPreviewObject(obj: any): void {
    if (!obj?.group) return;

    // Remove from outline effect
    obj.group.children.forEach((child: any) => {
      if (child instanceof Mesh) {
        this.engine?.outlineEffect?.selection.delete(child);
      }
    });

    // Reset emissive for merged meshes
    const merged = this.getMergedMesh(obj.group);
    if (merged) {
      merged.perFaceMaterial.setFacesEmissive(obj.faces, 0);
    }
  }

  private clearPreviewHighlights(): void {
    for (const obj of this.previewHighlightedObjects) {
      this.unhighlightPreviewObject(obj);
    }
    this.previewHighlightedObjects = [];
  }

  private getObjectsInScreenRect(x1: number, y1: number, x2: number, y2: number): any[] {
    const rect = this.canvas.getBoundingClientRect();
    const minX = Math.min(x1, x2);
    const maxX = Math.max(x1, x2);
    const minY = Math.min(y1, y2);
    const maxY = Math.max(y1, y2);

    const selectedObjects: any[] = [];

    for (const obj of this.objects) {
      // Skip not allowed part types and invisible objects
      if (this.notAllowedPartTypes.indexOf(obj.type) !== -1 || !obj.group.visible) {
        continue;
      }

      // Get the bounding box center of the object
      const box = new Box3().setFromObject(obj.group);
      const center = box.getCenter(new Vector3());

      // Project 3D point to screen coordinates
      const screenPos = center.clone().project(this.camera);

      // Convert from NDC (-1 to 1) to screen pixels
      const screenX = ((screenPos.x + 1) / 2) * this.canvas.clientWidth + rect.x;
      const screenY = ((-screenPos.y + 1) / 2) * this.canvas.clientHeight + rect.y;

      // Check if the screen position is within the selection rectangle
      if (screenX >= minX && screenX <= maxX && screenY >= minY && screenY <= maxY) {
        selectedObjects.push(obj);
      }
    }

    return selectedObjects;
  }
}
